import Icon from '@ant-design/icons';
import { Button, Table, TablePaginationConfig } from 'antd';
import Search from 'antd/lib/input/Search';
import { ColumnsType } from 'antd/lib/table';
import React, { Component } from 'react';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import skillApi from '../../../api/SkillApi';
import LayoutComponent from '../../../components/LayoutComponent/LayoutComponent';
import CustomContext from '../../../context/CustomContext';
import { WithRouterProps, withRouter } from '../../../hooks/WithRouter';
import { Page } from '../../../model/elements';
import { Skill } from '../../../model/entities';
import { ReactComponent as AddSvg } from '../../../resources/images/add.svg';
import { ReactComponent as SearchSvg } from '../../../resources/images/search.svg';
import notificationService from '../../../services/NotificationService';
import tableService from '../../../services/TableService';

class SkillsPage extends Component<WithRouterProps<Props>, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;

    constructor(props: WithRouterProps<Props>) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        try {
            this.setState({ loading: true });
            const skillsPage = await skillApi.list(0, tableService.pageSize, 'name', true);
            this.setState({ skillsPage, loading: false });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    list = async (pagination: TablePaginationConfig, filters: any, sorter: any) => {
        const { searchText } = this.state;
        try {
            this.setState({ loading: true });
            const page = pagination.current!;
            const pageSize = pagination.pageSize!;
            const sortField = sorter.field;
            const sortOrder = sorter.order === 'ascend';
            const skillsPage = await skillApi.list(page - 1, pageSize, sortField, sortOrder, undefined, searchText);
            this.setState({ skillsPage, loading: false });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    search = async (searchText: string) => {
        const { skillsPage } = this.state;
        try {
            this.setState({ loading: true });
            const pageSize = skillsPage!.size;
            const sortField = skillsPage!.sort.field!;
            const sortOrder = skillsPage!.sort.order!;
            const skillsPageNew = await skillApi.list(0, pageSize, sortField, sortOrder, undefined, searchText);
            this.setState({ skillsPage: skillsPageNew, searchText, loading: false });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    /*** COMPONENTS ***/

    renderContent = (): React.ReactElement | undefined => {
        return (
            <>
                {this.renderToolbar()}
                {this.renderTable()}
            </>
        );
    };

    renderToolbar = (): React.ReactElement | undefined => {
        const { intl } = this.props;
        const placeholder: string = intl.formatMessage({ id: 'skills.search' });

        return (
            <div className="toolbar">
                <Search
                    placeholder={placeholder}
                    onSearch={this.search}
                    size="large"
                    bordered={false}
                    addonBefore={<Icon component={SearchSvg} />}
                    className="search"
                />
                <Link to="/super-admin/skills/new">
                    <Button type="primary" size="large" icon={<Icon component={AddSvg} />}>
                        <FormattedMessage id="skills.new" tagName="span" />
                    </Button>
                </Link>
            </div>
        );
    };

    renderTable = (): React.ReactElement | undefined => {
        const { skillsPage, loading } = this.state;
        const items = skillsPage ? skillsPage.content : [];
        const columns: ColumnsType<Skill> = [
            {
                title: <FormattedMessage id="skills.name" />,
                dataIndex: 'name',
                key: 'name',
                sorter: true,
                defaultSortOrder: 'ascend',
                render: (value: string, item: Skill) => <Link to={`/super-admin/skills/${item.id}`}>{value}</Link>,
            },
            {
                title: <FormattedMessage id="skill.language" />,
                dataIndex: 'language',
                key: 'language',
                width: '110px',
                align: 'center',
                sorter: true,
                render: (value: string, item: Skill) => (
                    <Link to={`/super-admin/skills/${item.id}`}>{value.toUpperCase()}</Link>
                ),
            },
            {
                title: <FormattedMessage id="skill.filesNumber" />,
                dataIndex: 'filesNumber',
                key: 'filesNumber',
                width: '150px',
                align: 'right',
                render: (value: string, item: Skill) => <Link to={`/super-admin/skills/${item.id}`}>{value}</Link>,
            },
            {
                title: <FormattedMessage id="skill.questionsNumber" />,
                dataIndex: 'questionsNumber',
                key: 'questionsNumber',
                width: '150px',
                align: 'right',
                render: (value: string, item: Skill) => <Link to={`/super-admin/skills/${item.id}`}>{value}</Link>,
            },
        ];

        return (
            <Table
                dataSource={items}
                columns={columns}
                pagination={tableService.createPagination(skillsPage)}
                rowKey="id"
                onChange={this.list}
                sortDirections={['ascend']}
                showSorterTooltip={false}
                loading={loading}
            />
        );
    };

    render() {
        return <LayoutComponent pageId="skills">{this.renderContent()}</LayoutComponent>;
    }
}
export default injectIntl(withRouter<Props>(SkillsPage));

interface Props extends WrappedComponentProps {}

interface State {
    skillsPage?: Page<Skill>;
    loading?: boolean;
    searchText?: string;
}
